import React, { useState, useEffect, useRef } from 'react'
import EditCommonInformation from './EditCommonInformation';
import Address from './Address';
import { Tabs, Button, Spin } from 'antd';
import type { TabsProps } from 'antd';
import usePortalService from '../../services/PortalService';
import { ManagerNavbar } from '../../Components/Navbars';
import { useNavigate } from 'react-router-dom';
import { Company } from '../../types';

type commonInformationRefProps = {
    saveImages: () => Promise<void>,
    checkErrors: () => boolean
}

export const EditProfileCompany = () => {
    const navigate = useNavigate();
    const {updateCompanyInformation, createCompany, commonInformation, updateCompanyAddress} = usePortalService();

    const commonInformationRef = useRef<commonInformationRefProps>(null);
    const addressRef = useRef<any>(null);

    const [doesCompanyExist, setDoesCompanyExist] = useState(false);
    const [company, setCompany] = useState<Company | null>(null);
    const [openTab, setOpenTab] = useState('0');
    const [isSending, setIsSending] = useState(false);

    const [isCommonInformationChanged, setIsCommonInformationChanged] = useState(false);
    const [isImagesChanged, setImagesChanged] = useState(false);
    const [isAddresChanged, setIsAddreadChanged] = useState(false);

    const [companyName, setCompanyName] = useState('');
    const [website, setWebsite] = useState('');
    const [description, setDescription] = useState('');
    
    const [region, setRegion] = useState('');
    const [city, setCity] = useState('');
    const [street, setStreet] = useState('');
    const [house, setHouse] = useState('');

    const handleSave = async () => {
        setIsSending(true);
        try {
            if (commonInformationRef.current) {
                if (commonInformationRef.current.checkErrors()) {
                    setOpenTab('0');
                    return;
                }
            }
            if (addressRef.current) {
                if (addressRef.current.checkErrors()){
                    setOpenTab('1');
                    return;
                }
            }

            const updatePromises = [];
            
            if (doesCompanyExist) {
                if (isCommonInformationChanged) {
                    updatePromises.push(
                        updateCompanyInformation({
                            name: companyName,
                            site: website,
                            description,
                        })
                    );
                }
                if (isAddresChanged) {
                    updatePromises.push(
                        updateCompanyAddress({
                            region,
                            city,
                            street,
                            house,
                        })
                    );
                }
            } else {
                if (openTab === '0') {
                    setOpenTab('1');
                    return;
                }
                const result = createCompany({
                    name: companyName,
                    linkToSite: website,
                    description: description,
                    region,
                    city,
                    street,
                    house
                });
            }

            //было commonInformationRef.saveImages() c ошибкой
            const imgRes = await commonInformationRef.current?.saveImages();
            const results = await Promise.all([...updatePromises, ...(imgRes || [])]);

            const flattenedResults = results.flat();
            const allSuccessful = flattenedResults.every(result => result && result.success);

            if (allSuccessful) {
                navigate("/");
            }
        } catch (error) {
            console.error("Ошибка при сохранении", error);
        } finally {
            setIsSending(false);
        }
    };

    const items: TabsProps['items'] = [
        {
            key: '0',
            label: <span id="common-info-tab">Общая информация</span>,
            children: 
                <EditCommonInformation 
                    company={company as Company} 
                    ref={commonInformationRef}
                    companyName={companyName}
                    website={website}
                    description={description}
                    onCompanyNameChange={e => setCompanyName(e)}
                    onWebsiteChange={setWebsite}
                    onDescriptionChange={setDescription}
                    onDataChange={setIsCommonInformationChanged}
                >
                {isSending ?
                    <Spin tip="Сохранение">
                        <Button type="primary" style={{marginRight: '20px'}} onClick={handleSave}> {doesCompanyExist ? "Сохранить" : "Далее"} </Button>
                        <button className="grey-button" onClick={() => window.history.back()}> Отмена </button>
                    </Spin> 
                    : 
                    <>
                        <Button type="primary" style={{marginRight: '20px'}} onClick={handleSave}> {doesCompanyExist ? "Сохранить" : "Далее"} </Button>
                        <button className="grey-button" onClick={() => window.history.back()}> Отмена </button>
                    </>
                }
            </EditCommonInformation>,
        },
        {
            key: '1',
            label: <span id='address-tab'>Адрес</span>,
            children: <Address 
                    company={company as Company} 
                    ref={addressRef}
                    region={region}
                    city={city}
                    street={street}
                    house={house}
                    onRegionChange={setRegion}
                    onCityChange={setCity}
                    onStreetChange={setStreet}
                    onHouseChange={setHouse}
                    onDataChange={setIsAddreadChanged}
                >
                {isSending ?
                    <Spin tip="Сохранение">
                        <Button type="primary" style={{marginRight: '20px'}} onClick={handleSave}> {doesCompanyExist ? "Сохранить" : "Далее"} </Button>
                        <button className="grey-button" onClick={() => window.history.back()}> Отмена </button>
                    </Spin> 
                    : 
                    <>
                        <Button type="primary" style={{marginRight: '20px'}} onClick={handleSave}> {doesCompanyExist ? "Сохранить" : "Далее"} </Button>
                        <button className="grey-button" onClick={() => window.history.back()}> Отмена </button>
                    </>
                }
            </Address>,
        },
    ]

    useEffect(() => {
        commonInformation().then(result => {
            if (result.success) {
                setCompany(result.company);
                setDoesCompanyExist(true);
                setCompanyName(result.company.name);
                setWebsite(result.company.link);
                setDescription(result.company.description);
                setRegion(result.company.region)
                setCity(result.company.city)
                setStreet(result.company.street)
                setHouse(result.company.house)
            }
        });
    }, []);

    const tabHandler = (key: string) => {
        setOpenTab(key)
    };

    return (
        <>
            <ManagerNavbar />
            <div className="whole-page">
                <h1 style={{marginBottom: '24px'}}> О компании </h1>
                <Tabs items={items} style={{paddingRight:'8px'}} activeKey={openTab} onChange={tabHandler}/>
            </div>
        </>
    )
}